import React, { useCallback } from "react";
import { commitMutation, graphql, QueryRenderer, type RelayRefetchProp } from "react-relay";

import { faBolt } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import cx from "classnames";
import { debounce, noop } from "lodash";

import { ReactComponent as HubspotLogo } from "~/legacy-assets/images/logos/hubspot.svg";
import growthbook from "~/lib/ab/growthbook";
import { smart } from "~/lib/relayUtil";
import { environment } from "~/models";
import {
	HUBSPOT_MEETING_TEAM_NOTES_FLAG,
	HUBSPOT_TASKS_INTEGRATION_FLAG,
} from "~/stream/SideBar/CRMPanel/Hubspot/featureFlag";
import { Button } from "~/ui";
import { Modal, Switch } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";
import toast from "~/ui/toast";

import Settings from "../../../account/Integrations/Settings";
import { Label, Left, Right, Row, SectionTitle, SubLabel } from "../../SettingsModal/shared";
import { CRM_USER_SETTINGS_UPDATED_EVENT } from "../utils";
import { aboutHubspotIntegrationContent } from "./HubspotText";
import { getTooltipContent } from "./utils";

import type { HubspotIntegration_viewer$data as HubspotIntegrationViewer } from "~/__generated__/HubspotIntegration_viewer.graphql";
import type { HubspotIntegrationLegacyDisconnectMutation as DisconnectHubspotType } from "~/__generated__/HubspotIntegrationLegacyDisconnectMutation.graphql";
import type { HubspotIntegrationQuery } from "~/__generated__/HubspotIntegrationQuery.graphql";
import type {
	HubspotUpdateSettingsInput,
	HubspotIntegrationUpdateSettingsMutation as UpdateSettingsType,
} from "~/__generated__/HubspotIntegrationUpdateSettingsMutation.graphql";
import type { HubspotIntegrationUserConnectionMutation as ConnectHubspotType } from "~/__generated__/HubspotIntegrationUserConnectionMutation.graphql";

import settingsStyles from "~/settings/css/settings.module.css";

import styles from "./Hubspot.module.css";

const DisconnectLegacyHubspotMutation = graphql`
	mutation HubspotIntegrationLegacyDisconnectMutation($input: HubspotLegacyDisconnectInput!) {
		disconnectLegacyHubspot(input: $input) {
			ok
		}
	}
`;

const HubspotUserConnectionMutation = graphql`
	mutation HubspotIntegrationUserConnectionMutation($input: HubspotUserConnectionInput!) {
		connectUserHubspot(input: $input) {
			ok
			hubspotAccount {
				userConnected
				connectedUsersCount
			}
		}
	}
`;

interface DisconnectModalProps {
	onClose: () => void;
	relay: RelayRefetchProp;
	sendConnectionMutation?: (connected: boolean) => void;
}

const DisconnectModal: React.FunctionComponent<DisconnectModalProps> = props => {
	const { onClose, relay, sendConnectionMutation } = props;

	const onDisconnect = React.useCallback(() => {
		if (sendConnectionMutation) {
			sendConnectionMutation(false);
		} else {
			commitMutation<DisconnectHubspotType>(environment, {
				mutation: DisconnectLegacyHubspotMutation,
				variables: {
					input: {},
				},
				onCompleted: ({ disconnectLegacyHubspot }) => {
					if (disconnectLegacyHubspot?.ok) {
						toast.success(`HubSpot no longer has access to your account.`);
					} else {
						toast.error(`Sorry, something went wrong and we couldn't disconnect HubSpot.`);
					}
					relay.refetch({});
				},
			});
		}

		onClose();
	}, [onClose, relay, sendConnectionMutation]);

	return (
		<Modal onClose={onClose} width="31.25em">
			<Modal.Header noBottomBorder onClose={onClose}>
				<Modal.Title>Are you sure you want to disconnect HubSpot?</Modal.Title>
			</Modal.Header>
			<Modal.Body textOnly className={styles.removeIntegrationModalBody}>
				Disconnecting HubSpot will cause your notes to no longer have access to HubSpot.
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={onClose}>
					Cancel
				</Button>
				<Button size="normal" variant="destructive" onClick={onDisconnect}>
					Disconnect
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export type HubspotSettingsProps = {
	onBackClick?: () => void;
};

type HubspotSettingsInternalProps = HubspotSettingsProps & {
	viewer: HubspotIntegrationViewer;
	relay: RelayRefetchProp;
};

type HubspotSettingsInternalState = {
	autoSendNotesEnabled: boolean;
	includeRecapInAutoSendNotes: boolean;
	associateWithRelatedDeals: boolean;
	createHubspotTasksFromActionItems: boolean;
};

class HubspotSettings extends React.Component<HubspotSettingsInternalProps, HubspotSettingsInternalState> {
	isHubspotMeetingTeamNotesFeatureOn = growthbook.isOn(HUBSPOT_MEETING_TEAM_NOTES_FLAG);
	isHubspotTasksIntegrationFeatureOn = growthbook.isOn(HUBSPOT_TASKS_INTEGRATION_FLAG);

	static Fragments = {
		viewer: graphql`
			fragment HubspotIntegration_viewer on Viewer {
				hasAccessToCopilot
				user {
					internalAttributes {
						isAccountAdmin
					}
				}
				account {
					primaryAdmin {
						fullName
					}
					integrations {
						hubspot {
							accountConnected
							userConnected
						}
					}
					billingAccount {
						hasAccessToHubspotFeature: hasAccessToFeature(feature: hubspot_integration)
					}
				}
				integrations {
					hubspot {
						isConnected
						autoSendNotesEnabled
						associateWithRelatedDeals
						includeRecapInAutoSendNotes
						createHubspotTasksFromActionItems
					}
					salesforce {
						isConnected
					}
				}
			}
		`,
	};

	static Refetch = graphql`
		query HubspotIntegrationRefetchQuery {
			viewer {
				...HubspotIntegration_viewer
			}
		}
	`;

	constructor(props: HubspotSettingsInternalProps) {
		super(props);
		this.state = {
			autoSendNotesEnabled: Boolean(props.viewer.integrations.hubspot?.autoSendNotesEnabled),
			includeRecapInAutoSendNotes: Boolean(props.viewer.integrations.hubspot?.includeRecapInAutoSendNotes),
			associateWithRelatedDeals: Boolean(props.viewer.integrations.hubspot?.associateWithRelatedDeals),
			createHubspotTasksFromActionItems: Boolean(props.viewer.integrations.hubspot?.createHubspotTasksFromActionItems),
		};
	}

	script: HTMLScriptElement | undefined = undefined;

	onTabClick = noop;

	onDisconnectModalOpen = () => {
		this.props.relay.refetch({});
		const legacyConnected = Boolean(this.props.viewer.integrations.hubspot?.isConnected);

		__globalModalContext?.open(({ close }) => (
			<DisconnectModal
				onClose={close}
				relay={this.props.relay}
				sendConnectionMutation={legacyConnected ? undefined : this.sendConnectionMutation}
			/>
		));
	};

	sendConnectionMutation = (connected: boolean) => {
		commitMutation<ConnectHubspotType>(environment, {
			mutation: HubspotUserConnectionMutation,
			variables: {
				input: {
					connected: connected,
				},
			},
			onCompleted: ({ connectUserHubspot }) => {
				if (connectUserHubspot?.ok) {
					this.props.relay.refetch(
						{},
						null,
						() => {
							const {
								autoSendNotesEnabled,
								includeRecapInAutoSendNotes,
								associateWithRelatedDeals,
								createHubspotTasksFromActionItems,
							} = this.props.viewer.integrations.hubspot || {};

							this.setState({
								autoSendNotesEnabled: Boolean(autoSendNotesEnabled),
								includeRecapInAutoSendNotes: Boolean(includeRecapInAutoSendNotes),
								associateWithRelatedDeals: Boolean(associateWithRelatedDeals),
								createHubspotTasksFromActionItems: Boolean(createHubspotTasksFromActionItems),
							});
						},
						{ force: true },
					);

					if (connected) {
						toast.success("HubSpot successfully connected");
					} else {
						toast.success("HubSpot disconnected");
					}
				} else {
					toast.error("Sorry, something went wrong and we couldn't update your connection to HubSpot.");
				}
			},
		});
	};

	onConnect = async () => {
		this.sendConnectionMutation(true);
	};

	setAutoSendNotes = (autoSendNotesEnabled: boolean) => {
		this.setState({ autoSendNotesEnabled }, this.debouncedSaveSettings);
	};

	setAssociateWithRelatedDeals = (associateWithRelatedDeals: boolean) => {
		this.setState({ associateWithRelatedDeals }, this.debouncedSaveSettings);
	};

	setIncludeRecapInAutoSendNotes = (includeRecapInAutoSendNotes: boolean) => {
		this.setState({ includeRecapInAutoSendNotes }, this.debouncedSaveSettings);
	};

	setCreateHubspotTasksFromActionItems = (createHubspotTasksFromActionItems: boolean) => {
		this.setState({ createHubspotTasksFromActionItems }, this.debouncedSaveSettings);
	};

	saveSettings = () => {
		const {
			autoSendNotesEnabled,
			includeRecapInAutoSendNotes,
			associateWithRelatedDeals,
			createHubspotTasksFromActionItems,
		} = this.state;
		const input: HubspotUpdateSettingsInput = {
			autoSendNotesEnabled,
			includeRecapInAutoSendNotes,
			associateWithRelatedDeals,
			createHubspotTasksFromActionItems,
		};

		commitMutation<UpdateSettingsType>(environment, {
			mutation: graphql`
				mutation HubspotIntegrationUpdateSettingsMutation($input: HubspotUpdateSettingsInput!) {
					hubspotUpdateSettings(input: $input) {
						ok
					}
				}
			`,
			variables: {
				input,
			},
			onCompleted: ({ hubspotUpdateSettings }) => {
				if (hubspotUpdateSettings?.ok) {
					window.dispatchEvent(new Event(CRM_USER_SETTINGS_UPDATED_EVENT));
				}
			},
		});
	};

	debouncedSaveSettings = debounce(this.saveSettings, 500);

	render() {
		const { integrations, account, user, hasAccessToCopilot } = this.props.viewer;
		const legacyConnected = Boolean(integrations.hubspot?.isConnected);
		const userConnected = Boolean(account?.integrations.hubspot?.userConnected);
		const accountConnected = Boolean(account?.integrations.hubspot?.accountConnected);
		const userCanHubspot = legacyConnected || (userConnected && accountConnected);
		const paywalled = !account?.billingAccount.hasAccessToHubspotFeature;
		const isAdmin = user.internalAttributes?.isAccountAdmin ?? false;
		const primaryAdminFullName = account?.primaryAdmin?.fullName ?? "";
		const otherCRMConnected = !!this.props.viewer.integrations.salesforce?.isConnected;
		const automationAvailable = userCanHubspot && !paywalled;

		const disabledMessage = getTooltipContent(
			otherCRMConnected,
			accountConnected,
			legacyConnected,
			isAdmin,
			primaryAdminFullName,
		);

		const {
			autoSendNotesEnabled,
			includeRecapInAutoSendNotes,
			associateWithRelatedDeals,
			createHubspotTasksFromActionItems,
		} = this.state;

		return (
			<React.Fragment>
				<Settings
					name="HubSpot"
					connected={userCanHubspot}
					logo={<HubspotLogo height="24px" />}
					paywalledButton={paywalled ? "fellow_business_ai" : undefined}
					paywalledBanner={paywalled ? "fellow_business_ai" : undefined}
					paywallFeature="hubspot_integration"
					installText="Connect"
					tabs={[]}
					activeTab={""}
					onTabClick={this.onTabClick}
					onInstall={this.onConnect}
					onDisconnect={this.onDisconnectModalOpen}
					backPath="/settings/integrations/"
					installIcon={faBolt}
					disabledMessage={disabledMessage}
					onBackClick={this.props.onBackClick}
					showMoreActions={!paywalled}
					articleIdOnPaywall="5714773"
				>
					{aboutHubspotIntegrationContent}
					<section className={cx({ [styles.sectionDisabled]: !automationAvailable })}>
						<SectionTitle>Configurations</SectionTitle>
						<Row>
							<Left>
								<Label>Automatically send notes to HubSpot 3hrs after a meeting</Label>
								{/*  eslint-disable max-len */}
								<SubLabel>
									A <strong>{this.isHubspotMeetingTeamNotesFeatureOn ? "Meeting" : "Call"}</strong> will be created and
									associated to the <strong>Contacts</strong> on the calendar event and any <strong>Company</strong> or{" "}
									<strong>Deal</strong> associated to that contact.
								</SubLabel>
								{/*  eslint-enable max-len */}
							</Left>
							<Right>
								<Switch
									size="small"
									checked={autoSendNotesEnabled}
									onCheckedChange={this.setAutoSendNotes}
									disabled={!automationAvailable}
								/>
							</Right>
						</Row>
						<section className={cx(styles.subSettings, { [styles.sectionDisabled]: !autoSendNotesEnabled })}>
							<Row>
								<span className={settingsStyles.indentArrowImage} />
								<Left>
									<Label>
										Also associate to any <strong>Deals</strong> associated to the <strong>Company</strong>
									</Label>
								</Left>
								<Right>
									<Switch
										size="small"
										disabled={!autoSendNotesEnabled || !automationAvailable}
										checked={associateWithRelatedDeals}
										onCheckedChange={this.setAssociateWithRelatedDeals}
									/>
								</Right>
							</Row>
							{hasAccessToCopilot && (
								<Row>
									<span className={settingsStyles.indentArrowImage} />
									<Left>
										<Label>Include AI recap and link to recording</Label>
									</Left>
									<Right>
										<Switch
											size="small"
											disabled={!autoSendNotesEnabled || !automationAvailable}
											checked={includeRecapInAutoSendNotes}
											onCheckedChange={this.setIncludeRecapInAutoSendNotes}
										/>
									</Right>
								</Row>
							)}
							{this.isHubspotTasksIntegrationFeatureOn && (
								<Row>
									<span className={settingsStyles.indentArrowImage} />
									<Left>
										<Label>Create tasks from action items</Label>
									</Left>
									<Right>
										<Switch
											size="small"
											disabled={!autoSendNotesEnabled || !automationAvailable}
											checked={createHubspotTasksFromActionItems}
											onCheckedChange={this.setCreateHubspotTasksFromActionItems}
										/>
									</Right>
								</Row>
							)}
						</section>
					</section>
				</Settings>
			</React.Fragment>
		);
	}
}

const SmartHubspotSettings = smart(HubspotSettings);

function HubspotIntegrationRenderer(props: HubspotSettingsProps) {
	return (
		<QueryRenderer<HubspotIntegrationQuery>
			environment={environment}
			query={graphql`
				query HubspotIntegrationQuery {
					viewer {
						...HubspotIntegration_viewer
					}
				}
			`}
			variables={{}}
			render={useCallback(({ props: data }) => (data ? <SmartHubspotSettings {...data} {...props} /> : null), [props])}
		/>
	);
}

export default HubspotIntegrationRenderer;
